/**
 * Die Zuordnungen im Sheet „Zahlung erfassen" (`payment-allocations`, Spec
 * 04b, „Sheet Zahlung erfassen"). Zeigt die offenen Posten des gewählten
 * Mieters — `GET /billing/open-items?tenant_id=`, aus 04a wiederverwendet;
 * 04b baut dafür **keinen** eigenen Endpunkt (Befund W1).
 *
 * **Rein darstellend, was den Status angeht** (Spec 04b, „Sheet Zahlung
 * erfassen"): `open_cents`/`open_display` kommen vom Server, diese
 * Komponente rechnet nichts über den Status oder die Fälligkeit einer
 * Sollstellung — sie meldet nur den eingetippten Wert nach oben.
 * `PaymentSheet` bildet daraus „Nicht zugeordnet" (die einzige Rechnung im
 * Client, siehe dort).
 *
 * `Enter` in einem Betragsfeld ordnet den Rest zu — dasselbe wie ein Klick
 * auf „ganz zuordnen" (Spec 04b, „Tastatur").
 */


import { ApiError } from '@/lib/api'
import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import type { useOpenItems } from '@/features/billing/api'
import { FIELD_CLASS } from '@/components/FormField'

interface PaymentAllocationsProps {
  readonly query: ReturnType<typeof useOpenItems>
  readonly amounts: Readonly<Record<string, string>>
  readonly onAmountChange: (chargeId: string, value: string) => void
  readonly onFill: (chargeId: string, openCents: number) => void
}

export function PaymentAllocations({ query, amounts, onAmountChange, onFill }: PaymentAllocationsProps) {
  const accessDenied = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  return (
    <div data-testid="payment-allocations" className="flex flex-col gap-1">
      <span className="text-label text-muted">Zuordnungen</span>

      {query.isPending && <SkeletonRows testId="payment-allocations-loading" rows={3} />}

      {query.isError && (!query.isFetchNextPageError || accessDenied) && (
        <ErrorState
          testId="payment-allocations-error"
          retryTestId="payment-allocations-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          compact
          testId="payment-allocations-empty"
          title="Keine offenen Posten."
          hint="Die Zahlung wird als Vorauszahlung erfasst."
        />
      )}

      {!accessDenied && query.data !== undefined && query.data.items.length > 0 && (
        <ul className="border-border rounded-card divide-border divide-y border">
          {query.data.items.map((item) => (
            <li
              key={item.charge_id}
              data-testid={`payment-allocation-row-${item.charge_id}`}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
            >
              <span className="flex flex-col">
                <span className="text-body text-text">
                  {item.period_display}
                  {item.kind.value !== 'rent' ? ` · ${item.kind.label}` : ''}
                </span>
                <span data-numeric className="text-label text-muted">
                  offen {item.open_display}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <input
                  data-testid={`payment-allocation-amount-${item.charge_id}`}
                  data-numeric
                  type="text"
                  inputMode="decimal"
                  aria-label={`Zuordnung ${item.period_display}`}
                  value={amounts[item.charge_id] ?? ''}
                  onChange={(event) => {
                    onAmountChange(item.charge_id, event.target.value)
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return
                    event.preventDefault()
                    onFill(item.charge_id, item.open_cents)
                  }}
                  className={`text-text w-24 text-right ${FIELD_CLASS}`}
                />
                <button
                  type="button"
                  data-testid={`payment-allocation-fill-${item.charge_id}`}
                  onClick={() => {
                    onFill(item.charge_id, item.open_cents)
                  }}
                  className="text-accent text-label inline-flex min-h-(--tap-min) items-center hover:underline"
                >
                  ganz zuordnen
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {query.isFetchNextPageError && !accessDenied && (
        <ErrorState testId="payment-allocations-more-error" retryTestId="payment-allocations-more-retry" error={query.error} onRetry={() => { void query.fetchNextPage() }} />
      )}
      {query.hasNextPage && !query.isFetchNextPageError && !accessDenied && (
        <button type="button" data-testid="payment-allocations-more" disabled={query.isFetchingNextPage} onClick={() => { void query.fetchNextPage() }} className="border-border text-text border px-3">
          {query.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
        </button>
      )}
    </div>
  )
}
